import React, { useEffect, useState } from 'react';
import { Title, usePermissions } from 'react-admin';
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Chip } from '@mui/material';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';
import { useGithubConfig } from './ra_contentEditor';
import FolderIcon from '@mui/icons-material/Folder';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Real site traffic (pageviews/visitors) lives in Firebase Analytics (GA4),
// which only exposes a client-side event-logging SDK — reading aggregate
// reports back out requires the Analytics Data API from a backend with a
// service account, which this project doesn't have. The stats below are
// pulled straight from Firestore instead, which is real, live admin data.
const WORKFLOW_FILE = 'deploy.yml';

const STATUS_ICON = {
	completed_success: <CheckCircleIcon fontSize="small" color="success" />,
	completed_failure: <ErrorIcon fontSize="small" color="error" />,
	in_progress: <HourglassEmptyIcon fontSize="small" color="warning" />,
	queued: <HourglassEmptyIcon fontSize="small" color="warning" />,
};

const StatCard = ({ icon, label, value, loading }) => (
	<Card sx={{ minWidth: 160, flex: '1 1 160px' }}>
		<CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
			{icon}
			<Box>
				<Typography variant="h4">{loading ? <CircularProgress size={20} /> : value}</Typography>
				<Typography variant="body2" color="text.secondary">{label}</Typography>
			</Box>
		</CardContent>
	</Card>
);

const Dashboard = () => {
	const { permissions } = usePermissions();
	const { ghConfig } = useGithubConfig();
	const [counts, setCounts] = useState({ projects: null, services: null, emails: null, users: null });
	const [recentLeads, setRecentLeads] = useState([]);
	const [lastRun, setLastRun] = useState(null);
	const [runLoading, setRunLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const [projectsSnap, servicesSnap, allEmailsSnap, recentEmailsSnap] = await Promise.all([
				getDocs(collection(db, 'projects')),
				getDocs(collection(db, 'services')),
				getDocs(collection(db, 'emails')),
				getDocs(query(collection(db, 'emails'), orderBy('timestamp', 'desc'), limit(5))),
			]);
			if (cancelled) return;
			setCounts(prev => ({ ...prev, projects: projectsSnap.size, services: servicesSnap.size, emails: allEmailsSnap.size }));
			setRecentLeads(recentEmailsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

			if (permissions >= 99) {
				const usersSnap = await getDocs(collection(db, 'users'));
				if (!cancelled) setCounts(prev => ({ ...prev, users: usersSnap.size }));
			}
		})();
		return () => { cancelled = true; };
	}, [permissions]);

	useEffect(() => {
		if (!ghConfig) return;
		setRunLoading(true);
		fetch(
			`https://api.github.com/repos/${ghConfig.owner}/${ghConfig.repo}/actions/workflows/${WORKFLOW_FILE}/runs?per_page=1`,
			{
				headers: {
					Authorization: `Bearer ${ghConfig.pat}`,
					Accept: 'application/vnd.github+json',
					'X-GitHub-Api-Version': '2022-11-28',
				},
			}
		)
			.then(res => (res.ok ? res.json() : Promise.reject(res.status)))
			.then(data => setLastRun(data.workflow_runs?.[0] ?? null))
			.catch(() => {})
			.finally(() => setRunLoading(false));
	}, [ghConfig]);

	return (
		<Box p={2}>
			<Title title="Dashboard" />
			<Typography variant="h5" mb={2}>Dashboard</Typography>

			<Box display="flex" gap={2} flexWrap="wrap" mb={3}>
				<StatCard icon={<FolderIcon fontSize="large" color="primary" />} label="Projects" value={counts.projects} loading={counts.projects === null} />
				<StatCard icon={<DesignServicesIcon fontSize="large" color="primary" />} label="Services" value={counts.services} loading={counts.services === null} />
				<StatCard icon={<EmailIcon fontSize="large" color="primary" />} label="Email leads" value={counts.emails} loading={counts.emails === null} />
				{permissions >= 99 && (
					<StatCard icon={<PeopleIcon fontSize="large" color="primary" />} label="Users" value={counts.users} loading={counts.users === null} />
				)}
			</Box>

			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Typography variant="h6" mb={1}>Recent email leads</Typography>
							{recentLeads.length === 0 ? (
								<Typography variant="body2" color="text.secondary">No leads yet.</Typography>
							) : (
								recentLeads.map(lead => (
									<Box key={lead.id} display="flex" justifyContent="space-between" py={0.5} borderBottom="1px solid #eee">
										<Typography variant="body2">{lead.email}</Typography>
										<Typography variant="caption" color="text.secondary">
											{lead.timestamp ? new Date(lead.timestamp.seconds * 1000).toLocaleDateString() : ''}
										</Typography>
									</Box>
								))
							)}
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<RocketLaunchIcon />
								<Typography variant="h6">Last deploy</Typography>
							</Box>
							{!ghConfig ? (
								<Typography variant="body2" color="text.secondary">
									GitHub deploy isn't configured yet — set it up in GitHub Settings.
								</Typography>
							) : runLoading ? (
								<CircularProgress size={20} />
							) : lastRun ? (
								<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
									{STATUS_ICON[`${lastRun.status}_${lastRun.conclusion}`] ?? STATUS_ICON[lastRun.status] ?? null}
									<Typography variant="body2">
										<strong>{lastRun.status === 'completed' ? lastRun.conclusion : lastRun.status}</strong>
										{' · '}{new Date(lastRun.created_at).toLocaleString()}
									</Typography>
									<Chip label="View on GitHub" size="small" component="a" href={lastRun.html_url} target="_blank" rel="noopener noreferrer" clickable />
								</Box>
							) : (
								<Typography variant="body2" color="text.secondary">No deploy runs found yet.</Typography>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
};

export default Dashboard;
